import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import {
  getTPRMAuditEventsApi,
  getTPRMDashboardStatsApi,
  getTPRMFindingsApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import DomainPosturePanel from "./DomainPosturePanel";

const OPEN_FINDING_STATUSES = ["Open", "In Progress", "Pending Verification"];
const SEVERITY_ORDER = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const SEVERITY_STYLES = {
  Critical: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-200",
  Medium: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200",
  Low: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200",
};
const PANEL_CLASS =
  "min-w-0 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900";
const numberFormatter = new Intl.NumberFormat("en-US");

const normalizeResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.results) ? payload.results : [];
};

const formatNumber = (value) => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? "—"
    : numberFormatter.format(Number(value))
);

const formatDateTime = (value) => {
  if (!value) return "Unavailable";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Unavailable"
    : new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeZone: "UTC",
      }).format(parsed);
};

const SeverityBadge = ({ value }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${SEVERITY_STYLES[value] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"}`}>
    {value || "Unspecified"}
  </span>
);

const KpiCard = ({ icon: Icon, label, value, detail, onClick, tone = "slate" }) => {
  const color = {
    blue: "text-blue-700 dark:text-blue-300",
    amber: "text-amber-700 dark:text-amber-300",
    rose: "text-rose-700 dark:text-rose-300",
    slate: "text-slate-600 dark:text-slate-300",
    emerald: "text-emerald-700 dark:text-emerald-300",
  }[tone];
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</span>
        <Icon aria-hidden="true" className={`h-5 w-5 shrink-0 ${color}`} />
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
        {value}
      </p>
      {detail && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{detail}</p>}
    </>
  );
  const className = `h-full w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors dark:border-slate-800 dark:bg-slate-900 ${
    onClick
      ? "cursor-pointer hover:border-blue-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:hover:border-blue-800 dark:hover:bg-slate-800/70"
      : ""
  }`;

  return onClick ? (
    <button className={className} onClick={onClick} type="button">{content}</button>
  ) : (
    <article className={className}>{content}</article>
  );
};

const DistributionBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_2.5rem] items-center gap-3 text-sm">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <div
        aria-label={`${label}: ${formatNumber(count)} of ${formatNumber(total)} vendors`}
        className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
        role="img"
      >
        <div className={`h-full rounded-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-right font-medium tabular-nums text-slate-900 dark:text-white">
        {formatNumber(count)}
      </span>
    </div>
  );
};

const OverviewPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [priorityFindings, setPriorityFindings] = useState([]);
  const [activity, setActivity] = useState([]);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errors, setErrors] = useState({});

  const loadOverview = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setErrors({});

    // Promise.all rejects as a whole; use allSettled so independent dashboard
    // sections remain useful when one of the underlying APIs is unavailable.
    const settled = await Promise.allSettled([
      getTPRMDashboardStatsApi(),
      ...OPEN_FINDING_STATUSES.map((status) =>
        getTPRMFindingsApi({ status, limit: 50, offset: 0 }),
      ),
      getTPRMAuditEventsApi({ limit: 20, offset: 0 }),
    ]);
    const [portfolio, ...remaining] = settled;
    const auditEvents = remaining.pop();
    const findingPages = remaining;
    const nextErrors = {};

    if (portfolio.status === "fulfilled") {
      setStats(portfolio.value);
      setRefreshedAt(new Date().toISOString());
    } else {
      nextErrors.stats = portfolio.reason?.response?.data?.detail
        || portfolio.reason?.message
        || "Unable to load portfolio metrics.";
    }

    const successfulFindingPages = findingPages.filter(
      (result) => result.status === "fulfilled",
    );
    const findingResults = successfulFindingPages
      .flatMap((result) => normalizeResults(result.value));
    const successfulFindings = Array.from(new Map(
      findingResults.map((finding, index) => [
        finding.finding_id || `${finding.title || "finding"}-${index}`,
        finding,
      ]),
    ).values());
    setPriorityFindings(successfulFindings.sort((left, right) => {
      const severityDifference =
        (SEVERITY_ORDER[right.severity] || 0) - (SEVERITY_ORDER[left.severity] || 0);
      if (severityDifference) return severityDifference;
      const leftDue = left.remediation_due_date
        ? new Date(left.remediation_due_date).getTime()
        : Number.POSITIVE_INFINITY;
      const rightDue = right.remediation_due_date
        ? new Date(right.remediation_due_date).getTime()
        : Number.POSITIVE_INFINITY;
      return leftDue - rightDue;
    }));
    if (findingPages.every((result) => result.status === "rejected")) {
      nextErrors.findings = "Unable to load priority findings.";
    } else if (successfulFindingPages.length < findingPages.length) {
      nextErrors.findings = findingPages
        .filter((result) => result.status === "rejected")
        .map((result) => result.reason?.response?.data?.detail
          || result.reason?.message
          || "A remediation status could not be loaded.")
        .join(" ");
    }

    if (auditEvents.status === "fulfilled") {
      setActivity(normalizeResults(auditEvents.value).sort(
        (left, right) =>
          new Date(right.created_at || 0).getTime()
          - new Date(left.created_at || 0).getTime(),
      ));
    } else {
      setActivity([]);
      nextErrors.activity = auditEvents.reason?.response?.data?.detail
        || auditEvents.reason?.message
        || "Unable to load recent activity.";
    }

    setErrors(nextErrors);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const findingsColumns = useMemo(() => [
    {
      accessorKey: "severity",
      header: "Severity",
      size: 115,
      cell: ({ getValue }) => <SeverityBadge value={getValue()} />,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          options: ["Critical", "High", "Medium", "Low"].map((value) => ({ value, label: value })),
        },
      },
    },
    {
      accessorKey: "title",
      header: "Finding",
      size: 240,
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-slate-900 dark:text-white">{row.original.title || "Untitled finding"}</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {row.original.source_type || "Questionnaire"}
          </p>
        </div>
      ),
    },
    {
      id: "vendor",
      accessorFn: (finding) => finding.vendor_details?.name || finding.vendor || "—",
      header: "Vendor / service",
      size: 190,
      cell: ({ row, getValue }) => (
        <div>
          <p className="font-medium text-slate-800 dark:text-slate-200">{getValue()}</p>
          {row.original.service_name && (
            <p className="text-xs text-slate-500 dark:text-slate-400">{row.original.service_name}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: "remediation_owner",
      header: "Owner",
      size: 145,
      cell: ({ getValue }) => getValue() || "Unassigned",
    },
    {
      accessorKey: "remediation_due_date",
      header: "Due",
      size: 130,
      cell: ({ getValue, row }) => {
        const dueDate = getValue();
        const dueTime = dueDate ? new Date(dueDate).getTime() : Number.NaN;
        const overdue = Number.isFinite(dueTime)
          && dueTime < Date.now()
          && OPEN_FINDING_STATUSES.includes(row.original.remediation_status);
        return (
          <div>
            <span>{formatDate(dueDate)}</span>
            {overdue && (
              <span className="ml-1.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
                Overdue
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "remediation_status",
      header: "Status",
      size: 155,
      meta: {
        filter: {
          variant: "select",
          label: "Remediation status",
          options: OPEN_FINDING_STATUSES.map((value) => ({ value, label: value })),
        },
      },
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      size: 135,
      cell: ({ row }) => (
        <button
          className="rounded-md border border-blue-200 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-blue-900 dark:text-blue-300 dark:hover:bg-slate-800"
          onClick={() => navigate(`/ai-tprm?tab=risk-register&finding_id=${encodeURIComponent(row.original.finding_id)}`)}
          type="button"
        >
          Inspect finding
        </button>
      ),
    },
  ], [navigate]);

  if (loading && !stats && !errors.stats) {
    return <PageLoader title="Loading TPRM overview..." minH="min-h-[40vh]" />;
  }

  const navigateTo = (tab) => navigate(`/ai-tprm?tab=${tab}`);
  const vendorTotal = Number(stats?.total_vendors) || 0;
  const riskRows = [
    { label: "Critical", count: stats?.critical_risk_count, color: "bg-rose-600" },
    { label: "High", count: stats?.high_risk_count, color: "bg-orange-500" },
    { label: "Medium", count: stats?.medium_risk_count, color: "bg-amber-400" },
    { label: "Low", count: stats?.low_risk_count, color: "bg-emerald-500" },
    { label: "Unassessed", count: stats?.unassessed_risk_count, color: "bg-slate-400" },
  ];
  const scan = stats?.latest_domain_scan;
  const visibleFindings = priorityFindings.slice(0, 20);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-slate-800 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700 dark:text-blue-300">
            Third-party risk management
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
            Third-Party Risk Overview
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            Portfolio exposure, assessment progress, and remediation requiring attention.
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400" aria-live="polite">
            Data refreshed {refreshedAt ? formatDateTime(refreshedAt) : "—"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            aria-label="Refresh"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            disabled={refreshing}
            onClick={() => loadOverview({ refresh: true })}
            type="button"
          >
            <ArrowPathIcon aria-hidden="true" className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
          <button
            aria-label="Scan Domain"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
            onClick={() => document.getElementById("tprm-domain-posture")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            type="button"
          >
            <ShieldCheckIcon aria-hidden="true" className="h-4 w-4" />
            Scan Domain
          </button>
        </div>
      </header>

      {Object.keys(errors).length > 0 && (
        <section
          className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/30 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              {stats ? "Some overview data may be incomplete or stale" : "Overview data could not be loaded"}
            </p>
            <p className="mt-0.5 text-amber-800 dark:text-amber-300">
              {Object.values(errors).join(" ")}
            </p>
          </div>
          <button
            className="shrink-0 rounded-md border border-amber-300 px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-950"
            disabled={refreshing}
            onClick={() => loadOverview({ refresh: true })}
            type="button"
          >
            Retry
          </button>
        </section>
      )}

      {stats && (
        <>
          <section aria-label="Portfolio key metrics">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <KpiCard
                detail={`${formatNumber(stats.active_vendors)} active`}
                icon={BuildingOffice2Icon}
                label="Total vendors"
                onClick={() => navigateTo("vendors")}
                value={formatNumber(stats.total_vendors)}
              />
              <KpiCard
                detail={stats.total_vendors
                  ? `${formatNumber(stats.assessed_vendors)} assessed of ${formatNumber(stats.total_vendors)} vendors`
                  : "No vendors in scope"}
                icon={ShieldCheckIcon}
                label="Assessed vendor coverage"
                onClick={() => navigateTo("vendors")}
                tone="blue"
                value={stats.assessment_coverage_rate === null || stats.assessment_coverage_rate === undefined
                  ? "—"
                  : `${formatNumber(stats.assessment_coverage_rate)}%`}
              />
              <KpiCard
                icon={ClockIcon}
                label="Awaiting vendor response"
                onClick={() => navigateTo("responses")}
                tone="amber"
                value={formatNumber(stats.assessments_awaiting_vendor_response)}
              />
              <KpiCard
                icon={CheckCircleIcon}
                label="Awaiting internal review"
                onClick={() => navigateTo("responses")}
                tone="blue"
                value={formatNumber(stats.assessments_awaiting_internal_review)}
              />
              <KpiCard
                icon={ShieldExclamationIcon}
                label="Open high / critical findings"
                onClick={() => navigateTo("risk-register")}
                tone="rose"
                value={formatNumber(stats.open_high_critical_findings)}
              />
              <KpiCard
                icon={ExclamationTriangleIcon}
                label="Overdue remediation"
                onClick={() => navigateTo("risk-register")}
                tone="rose"
                value={formatNumber(stats.overdue_remediation_findings)}
              />
            </div>
          </section>

          <section aria-label="Portfolio analysis" className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
            <article className={`${PANEL_CLASS} p-4 sm:p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-slate-950 dark:text-white">Vendor risk distribution</h2>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Current vendor tiers · {formatNumber(vendorTotal)} total vendors
                  </p>
                </div>
                <button
                  className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                  onClick={() => navigateTo("vendors")}
                  type="button"
                >
                  View vendors
                </button>
              </div>
              {vendorTotal === 0 ? (
                <p className="mt-5 rounded-lg bg-slate-50 px-3 py-4 text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                  No vendor risk tiers are available because there are no vendors in scope.
                </p>
              ) : (
                <div className="mt-5 space-y-3">
                  {riskRows.map((row) => (
                    <DistributionBar
                      key={row.label}
                      color={row.color}
                      count={Number(row.count) || 0}
                      label={row.label}
                      total={vendorTotal}
                    />
                  ))}
                </div>
              )}
            </article>

            <article className={`${PANEL_CLASS} p-4 sm:p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-slate-950 dark:text-white">Assessment pipeline</h2>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Counts returned by the assessment portfolio API
                  </p>
                </div>
                <button
                  className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                  onClick={() => navigateTo("assessments")}
                  type="button"
                >
                  View assessments
                </button>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  {
                    label: "Draft",
                    count: stats.draft_assessments,
                    style: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50",
                  },
                  {
                    label: "Awaiting vendor response",
                    count: stats.assessments_awaiting_vendor_response,
                    style: "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20",
                  },
                  {
                    label: "Awaiting internal review",
                    count: stats.assessments_awaiting_internal_review,
                    style: "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20",
                  },
                  {
                    label: "Changes requested",
                    count: stats.assessments_changes_requested,
                    style: "border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/20",
                  },
                  {
                    label: "Completed",
                    count: stats.completed_assessments,
                    style: "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20",
                  },
                ].map((stage) => (
                  <button
                    className={`rounded-lg border p-4 text-left transition-colors hover:border-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${stage.style}`}
                    key={stage.label}
                    onClick={() => navigateTo("responses")}
                    type="button"
                  >
                    <span className="text-sm text-slate-600 dark:text-slate-300">{stage.label}</span>
                    <span className="mt-2 block text-2xl font-semibold tabular-nums text-slate-950 dark:text-white">
                      {formatNumber(stage.count)}
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {formatNumber(stats.pending_assessments)} assessments are in an active, non-completed state.
              </p>
            </article>
          </section>

          <section
            aria-label="Domain security posture summary"
            className={`${PANEL_CLASS} overflow-hidden`}
          >
            <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-semibold text-slate-950 dark:text-white">Latest domain scan</h2>
                  {scan && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {scan.status}
                    </span>
                  )}
                </div>
                {scan ? (
                  <>
                    <p className="mt-1 break-words text-sm text-slate-600 dark:text-slate-300">
                      {scan.target_hostname} · {scan.vendor_name}
                      {scan.service_name ? ` · ${scan.service_name}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {scan.status === "Completed" ? "Completed" : "Started"} {formatDateTime(scan.completed_at || scan.started_at)}
                      {" · "}{formatNumber(scan.finding_count)} findings in latest scan
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    No scans are recorded in this scope. Start an authorized, bounded check for a vendor&apos;s public hostname.
                  </p>
                )}
              </div>
              <div className="grid shrink-0 grid-cols-2 gap-x-5 gap-y-2 text-xs sm:min-w-64">
                {["critical", "high", "medium", "low"].map((severity) => (
                  <div className="flex justify-between gap-3" key={severity}>
                    <span className="capitalize text-slate-500 dark:text-slate-400">{severity}</span>
                    <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
                      {scan ? formatNumber(scan.severity_counts?.[severity] ?? 0) : "—"}
                    </span>
                  </div>
                ))}
                <div className="col-span-2 mt-1 border-t border-slate-100 pt-2 text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  Last successful scan: {stats.last_successful_domain_scan_at
                    ? formatDateTime(stats.last_successful_domain_scan_at)
                    : "No successful scan recorded"}
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-400">
              Bounded DNS, TLS, redirect, and security-header observations only. A completed scan is not a penetration test and does not establish that a domain is vulnerability-free.
            </div>
          </section>

          <section aria-label="Priority remediation findings" className={`${PANEL_CLASS} min-w-0 p-4 sm:p-5`}>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-semibold text-slate-950 dark:text-white">Priority remediation</h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Open findings prioritized by severity and then due date.
                </p>
              </div>
              <button
                className="self-start text-sm font-medium text-blue-700 hover:underline dark:text-blue-300 sm:self-auto"
                onClick={() => navigateTo("risk-register")}
                type="button"
              >
                Open Risk Register
              </button>
            </div>
            {errors.findings && (
              <p className="mb-3 text-sm text-amber-700 dark:text-amber-300" role="status">{errors.findings}</p>
            )}
            <DataTable
              columns={findingsColumns}
              data={visibleFindings}
              emptyMessage="No open remediation findings are available."
              enableColumnFilters
              enableColumnVisibility={false}
              enableGlobalFilter={false}
              enableSorting={false}
              getRowId={(finding) => finding.finding_id}
              initialPageSize={5}
            />
          </section>

          <section aria-label="Recent TPRM activity" className={`${PANEL_CLASS} p-4 sm:p-5`}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-950 dark:text-white">Recent activity</h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Audited TPRM actions in your authorized scope.
                </p>
              </div>
              <button
                className="shrink-0 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                onClick={() => navigateTo("audit-trail")}
                type="button"
              >
                View audit trail
              </button>
            </div>
            {errors.activity && (
              <p className="mb-3 text-sm text-amber-700 dark:text-amber-300" role="status">{errors.activity}</p>
            )}
            {activity.length ? (
              <ol className="divide-y divide-slate-100 dark:divide-slate-800">
                {activity.slice(0, 6).map((event, index) => (
                  <li className="flex flex-col gap-1 py-3 first:pt-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4" key={event.id || `${event.resource_id}-${event.action}-${index}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                        {(event.action || "TPRM event").replaceAll("_", " ")}
                        <span className="font-normal text-slate-500 dark:text-slate-400">
                          {" "}· {event.resource_type || "resource"} {event.resource_id || ""}
                        </span>
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Actor {event.actor_id || "Unavailable"}
                      </p>
                    </div>
                    <time className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400" dateTime={event.created_at || undefined}>
                      {formatDateTime(event.created_at)}
                    </time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-lg bg-slate-50 px-3 py-4 text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                No audit activity is available in this scope.
              </p>
            )}
          </section>
        </>
      )}

      <div id="tprm-domain-posture">
        <DomainPosturePanel />
      </div>
    </div>
  );
};

export default OverviewPage;
